import { decodeToken } from '../user/token';

const DEFAULT_REFRESH_INTERVAL_MS = 300000;
const MS_PER_SECOND = 1000;
const VIEWER_ID_TAIL_LENGTH = 12;

export interface AiPersonalisationModuleDeps {
    isFeatureEnabled: () => boolean | null;
    getEndpointUrl: () => string | undefined;
    getToken: () => string;
    getCountry: () => string;
    getLanguage: () => string;
    fetch: typeof window.fetch;
}

export const createAiPersonalisationModule = ({
    isFeatureEnabled,
    getEndpointUrl,
    getToken,
    getCountry,
    getLanguage,
    fetch,
}: AiPersonalisationModuleDeps) => {
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    let active = false;

    const scheduleNextHeartbeat = (delayMs: number) => {
        if (!active) return;
        timeoutId = setTimeout(sendHeartbeat, delayMs);
    };

    const sendHeartbeat = () => {
        const url = getEndpointUrl();
        const token = getToken();

        if (!url || !token) {
            scheduleNextHeartbeat(DEFAULT_REFRESH_INTERVAL_MS);
            return;
        }

        const decoded = decodeToken(token);
        if (!decoded?.user) {
            scheduleNextHeartbeat(DEFAULT_REFRESH_INTERVAL_MS);
            return;
        }

        const viewerId: string =
            decoded.viewerId ??
            (decoded.user as string).slice(-VIEWER_ID_TAIL_LENGTH);

        const entitlementSets: Array<{ id?: string }> =
            decoded.entitlements?.entitlementSets ?? [];
        const userEntitlements = entitlementSets
            .map(s => s?.id ?? '')
            .filter(Boolean)
            .join(',');

        const params = new URLSearchParams({
            viewerId,
            user: decoded.user as string,
            platform: 'web',
            country: getCountry(),
            language: getLanguage(),
            userEntitlements,
        });

        fetch(`${url}?${params.toString()}`)
            .then(response => response.json())
            .then((data: { RefreshInterval?: number }) => {
                const interval =
                    typeof data?.RefreshInterval === 'number'
                        ? data.RefreshInterval * MS_PER_SECOND
                        : DEFAULT_REFRESH_INTERVAL_MS;
                scheduleNextHeartbeat(interval);
            })
            .catch(() => {
                scheduleNextHeartbeat(DEFAULT_REFRESH_INTERVAL_MS);
            });
    };

    return {
        start(): void {
            if (!isFeatureEnabled() || !getToken()) return;
            if (active) stop();
            active = true;
            sendHeartbeat();
        },

        stop(): void {
            active = false;
            clearTimeout(timeoutId);
            timeoutId = undefined;
        },
    };
};
